import type { ReactNode } from 'react'
import { type MarketingLocale, useLocalized } from '~/lib/marketing-locale'
import { ScrollReveal } from './scroll-reveal'
import { TruchetField } from './truchet-field'

/*
 * Contracts — the "blindado" contract that auto-fills professional
 * registration, consultant scope and payment schedule. A signed contract
 * auto-opens the project.
 *
 * This is the section where the two locales diverge most in substance, not
 * just wording: the Portuguese contract is built around CAU/RRT/CREA
 * registration, CPF, and Pix/boleto, none of which exist for a US or UK
 * practice. The English version speaks to state licensure, a certificate of
 * insurance, and ACH/card — the equivalent protections, not a translation of
 * the Brazilian ones.
 */

type Toggle = { label: string; on: boolean }
type Bullet = { lead: string; rest: string }

type ContractsContent = {
  eyebrow: string
  heading: string
  intro: string
  badge: string
  contractTitle: string
  parties: string
  servicesLabel: string
  services: readonly Toggle[]
  consultantsLabel: string
  consultants: readonly Toggle[]
  registrationLabel: string
  registrationRows: readonly { label: string; value: string }[]
  paymentLabel: string
  totalLabel: string
  totalValue: string
  paymentTerms: string
  onSignTitle: string
  onSignBody: string
  bullets: readonly Bullet[]
}

const CONTENT: Record<MarketingLocale, ContractsContent> = {
  en: {
    badge: 'AIRTIGHT CONTRACT · v3.2',
    bullets: [
      {
        lead: 'Request details by link.',
        rest: ' Send it to the client to fill in their own details — legal name, address, entity — and the contract assembles itself.',
      },
      {
        lead: 'Phase sign-off letters.',
        rest: ' Every phase delivered closes with a signed acceptance. No ambiguity about who approved what.',
      },
      {
        lead: 'Amendments with version history.',
        rest: ' Scope changed? The amendment stays linked to the contract and earlier revisions are kept.',
      },
      {
        lead: 'Legal checklist.',
        rest: ' Contract-safety items to review before signing: scope, intellectual property, termination, governing law, schedule.',
      },
      {
        lead: 'Legally valid e-signature.',
        rest: ' The client signs from their phone. You keep the record with date, IP, and geolocation.',
      },
    ],
    consultants: [
      { label: 'Structural', on: true },
      { label: 'Electrical', on: true },
      { label: 'Plumbing', on: true },
      { label: 'Landscape', on: false },
      { label: 'Lighting', on: true },
    ],
    consultantsLabel: 'CONSULTANTS',
    contractTitle: 'Architectural Services Agreement',
    eyebrow: 'CONTRACTS',
    heading: 'Contracts with the cover an architecture practice actually needs',
    intro:
      'Licensure, insurance, consultant scope, witnesses, governing law, phases, deliverables. Every clause where the practice needs protecting.',
    onSignBody: 'creates the project · standard folder · receivables in Finance',
    onSignTitle: 'Once signed',
    parties: 'Whitfield Studio ↔ The Vaughans · June 18, 2026',
    paymentLabel: 'PAYMENT',
    paymentTerms: '20% deposit · 6 × $6,400 · ACH or card',
    registrationLabel: 'PROFESSIONAL REGISTRATION',
    registrationRows: [
      { label: 'State license', value: 'AR-9384572' },
      { label: 'Certificate of insurance', value: 'on file' },
    ],
    services: [
      { label: 'Architecture', on: true },
      { label: 'Interior design', on: true },
      { label: 'Renovation', on: false },
      { label: 'Consultation', on: false },
    ],
    servicesLabel: 'PRIMARY SERVICES',
    totalLabel: 'Total',
    totalValue: '$48,000',
  },
  'pt-BR': {
    badge: 'CONTRATO BLINDADO · v3.2',
    bullets: [
      {
        lead: 'Solicitar dados pelo link.',
        rest: ' Mande para o cliente preencher os próprios dados, CPF, endereço, doc, e o contrato monta sozinho.',
      },
      {
        lead: 'Termo de aprovação por etapa.',
        rest: ' Cada fase entregue fecha com termo assinado. Sem ambiguidade no “quem aprovou o quê”.',
      },
      {
        lead: 'Aditivo contratual com versionamento.',
        rest: ' Mudou o escopo? O aditivo fica linkado ao contrato e revisões anteriores ficam guardadas.',
      },
      {
        lead: 'Checklist jurídico.',
        rest: ' Itens de segurança contratual para conferir antes de assinar, escopo, propriedade intelectual, rescisão, foro, cronograma.',
      },
      {
        lead: 'Assinatura digital com validade.',
        rest: ' Cliente assina pelo celular. Fica o registro com data, IP e geolocalização.',
      },
    ],
    consultants: [
      { label: 'Estrutural', on: true },
      { label: 'Elétrico', on: true },
      { label: 'Hidrossanitário', on: true },
      { label: 'Paisagismo', on: false },
      { label: 'Luminotécnico', on: true },
    ],
    consultantsLabel: 'COMPLEMENTARES',
    contractTitle: 'Prestação de Serviços de Arquitetura',
    eyebrow: 'CONTRATOS',
    heading: 'Contratos com a blindagem que arquitetura no Brasil exige',
    intro:
      'CAU, RRT, projetos complementares, testemunhas, foro, etapas, entregáveis. Cada cláusula no lugar que o escritório precisa proteger.',
    onSignBody: 'cria o projeto · pasta padrão · recebíveis no Financeiro',
    onSignTitle: 'Quando assinado',
    parties: 'Estúdio Lopes ↔ Família Vieira · 18 de junho de 2026',
    paymentLabel: 'PAGAMENTO',
    paymentTerms: 'Entrada 20% · 6× R$ 6.400 · Pix ou boleto',
    registrationLabel: 'REGISTRO PROFISSIONAL',
    registrationRows: [
      { label: 'CAU/SP', value: 'A28394-7' },
      { label: 'ART (CREA)', value: 'a emitir' },
    ],
    services: [
      { label: 'Arquitetura', on: true },
      { label: 'Design de interiores', on: true },
      { label: 'Reforma', on: false },
      { label: 'Consultoria', on: false },
    ],
    servicesLabel: 'PROJETOS PRINCIPAIS',
    totalLabel: 'Valor total',
    totalValue: 'R$ 48.000',
  },
}

const ContractMockup = ({ content }: { content: ContractsContent }) => (
  <div className="overflow-hidden rounded-2xl border border-[#ececec] bg-white shadow-[0_24px_60px_-26px_rgba(20,20,40,0.28)]">
    <div className="border-[#f1f1f1] border-b bg-[#1c1c20] px-5 py-4 text-white">
      <p className="text-[10.5px] text-white/60 tracking-[0.18em]">{content.badge}</p>
      <p className="mt-2 font-bold text-[16px] leading-tight">{content.contractTitle}</p>
      <p className="mt-1 text-[11px] text-white/70">{content.parties}</p>
    </div>

    <div className="grid grid-cols-2 gap-0">
      <div className="border-[#f1f1f1] border-r p-5">
        <p className="text-[#9a9aa0] text-[10px] tracking-[0.18em]">{content.servicesLabel}</p>
        <div className="mt-2 space-y-1.5">
          {content.services.map((row) => (
            <div className="flex items-center gap-2 text-[11.5px]" key={row.label}>
              <span
                className="flex size-[14px] items-center justify-center rounded-[4px] text-[8px] text-white"
                style={{
                  background: row.on ? '#1c1c20' : 'transparent',
                  border: row.on ? 'none' : '1.5px solid #d4d4d6',
                }}
              >
                {row.on ? '✓' : ''}
              </span>
              <span className={row.on ? 'text-[#1c1c20]' : 'text-[#9a9aa0]'}>{row.label}</span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-[#9a9aa0] text-[10px] tracking-[0.18em]">
          {content.consultantsLabel}
        </p>
        <div className="mt-2 flex flex-wrap gap-1">
          {content.consultants.map((tag) => (
            <span
              className="rounded-md px-2 py-1 font-medium text-[10.5px]"
              key={tag.label}
              style={{
                background: tag.on ? '#1c1c20' : '#fafafa',
                border: tag.on ? 'none' : '1px solid #ececec',
                color: tag.on ? '#fff' : '#9a9aa0',
              }}
            >
              {tag.label}
            </span>
          ))}
        </div>
      </div>

      <div className="p-5">
        <p className="text-[#9a9aa0] text-[10px] tracking-[0.18em]">{content.registrationLabel}</p>
        <div className="mt-2 space-y-1.5 text-[11.5px]">
          {content.registrationRows.map((row) => (
            <div className="flex justify-between" key={row.label}>
              <span className="text-[#43434a]">{row.label}</span>
              <span className="font-medium text-[#1c1c20]">{row.value}</span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-[#9a9aa0] text-[10px] tracking-[0.18em]">{content.paymentLabel}</p>
        <div className="mt-2 rounded-lg bg-[#fafafa] p-2.5 text-[11.5px]">
          <div className="flex justify-between">
            <span className="text-[#43434a]">{content.totalLabel}</span>
            <span className="font-bold text-[#1c1c20]">{content.totalValue}</span>
          </div>
          <div className="mt-1 text-[#9a9aa0] text-[10.5px]">{content.paymentTerms}</div>
        </div>
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-[#1c1c20] bg-[#1c1c20] p-2.5 text-white">
          <span className="flex size-7 items-center justify-center rounded-md bg-white/10 text-[12px]">
            ✓
          </span>
          <div>
            <p className="font-semibold text-[10.5px]">{content.onSignTitle}</p>
            <p className="text-[10px] text-white/70">{content.onSignBody}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
)

const BulletItem = ({ children }: { children: ReactNode }) => (
  <li className="flex gap-2.5">
    <span className="mt-2 size-[6px] shrink-0 rounded-full bg-[#1c1c20]" />
    <span className="text-[#43434a] text-[15px] leading-relaxed">{children}</span>
  </li>
)

export const ContractsSection = () => {
  const content = useLocalized(CONTENT)

  return (
    <section className="relative overflow-hidden bg-white py-28">
      <TruchetField
        className="absolute inset-0 h-full w-full text-[#0b0b0d] opacity-[0.05]"
        seed="contracts"
      />
      <div className="relative mx-auto max-w-[1120px] px-6">
        <ScrollReveal className="text-center">
          <p className="text-[#1c1c20] text-[12px] tracking-[0.2em]">{content.eyebrow}</p>
          <h2 className="mx-auto mt-3 max-w-[860px] font-display text-[#0b0b0d] text-[clamp(40px,6vw,58px)] leading-[1.06] tracking-[-0.01em]">
            {content.heading}
          </h2>
          <p className="mx-auto mt-5 max-w-[640px] text-[#6c6c72] text-[clamp(16px,2.2vw,20px)]">
            {content.intro}
          </p>
        </ScrollReveal>

        <div className="mt-14 grid items-start gap-10 md:grid-cols-[1.2fr_1fr]">
          <ScrollReveal>
            <ContractMockup content={content} />
          </ScrollReveal>
          <ScrollReveal delay={120}>
            <ul className="space-y-3">
              {content.bullets.map((bullet) => (
                <BulletItem key={bullet.lead}>
                  <span className="font-semibold text-[#1c1c20]">{bullet.lead}</span>
                  {bullet.rest}
                </BulletItem>
              ))}
            </ul>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
